import { useEffect, useState } from 'react'
import { Users, UserCircle, Monitor, AlertTriangle, CheckCircle2, Infinity } from 'lucide-react'
import { apiFetch } from '@/api/client'

interface PlanResource {
  key: string
  label: string
  icon: string
  current: number
  limit: number
}

interface PlanUsage {
  enforcement: boolean
  resources: PlanResource[]
}

const ICON_MAP: Record<string, React.ReactNode> = {
  users:   <UserCircle className="w-5 h-5" />,
  members: <Users      className="w-5 h-5" />,
  devices: <Monitor    className="w-5 h-5" />,
}

function ResourceCard({ r, enforcement }: { r: PlanResource; enforcement: boolean }) {
  const pct       = r.limit > 0 ? Math.min(100, Math.round((r.current / r.limit) * 100)) : 0
  const atLimit   = enforcement && r.current >= r.limit
  const nearLimit = enforcement && pct >= 80 && !atLimit

  const color = atLimit
    ? { bar: 'bg-red-500',   text: 'text-red-500',   bg: 'bg-red-500/10',   border: 'border-red-500/20',   icon: 'text-red-500'   }
    : nearLimit
    ? { bar: 'bg-amber-400', text: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: 'text-amber-500' }
    : { bar: 'bg-accent',    text: 'text-accent',    bg: 'bg-accent/10',    border: 'border-accent/15',    icon: 'text-accent'    }

  return (
    <div className={`bg-surface border rounded-2xl p-5 flex flex-col gap-4 ${atLimit ? 'border-red-500/30' : nearLimit ? 'border-amber-500/30' : 'border-border'}`}>
      {/* Top row */}
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color.bg} border ${color.border}`}>
          <span className={color.icon}>
            {ICON_MAP[r.icon] ?? <Monitor className="w-5 h-5" />}
          </span>
        </div>

        {atLimit ? (
          <span className="flex items-center gap-1 text-[10px] font-bold text-red-500 bg-red-500/10 border border-red-500/20 px-2 py-1 rounded-full">
            <AlertTriangle className="w-3 h-3" /> Límite
          </span>
        ) : nearLimit ? (
          <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-full">
            <AlertTriangle className="w-3 h-3" /> Casi lleno
          </span>
        ) : !enforcement ? (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-text-muted bg-background border border-border px-2 py-1 rounded-full">
            <Infinity className="w-3 h-3" /> Sin límite
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> OK
          </span>
        )}
      </div>

      {/* Count */}
      <div>
        <p className={`text-3xl font-black leading-none tabular-nums ${color.text}`}>{r.current}</p>
        <p className="text-text-muted text-xs mt-1">
          {r.label}
          <span className="ml-1">· límite {r.limit}</span>
        </p>
      </div>

      {/* Bar */}
      <div className="flex flex-col gap-1.5">
        <div className="h-2 bg-background rounded-full overflow-hidden border border-border">
          <div
            className={`h-full rounded-full transition-all duration-500 ${color.bar}`}
            style={{ width: enforcement ? `${pct}%` : '100%', opacity: enforcement ? 1 : 0.25 }}
          />
        </div>
        {enforcement && (
          <p className={`text-[10px] font-semibold tabular-nums text-right ${color.text}`}>{pct}%</p>
        )}
      </div>
    </div>
  )
}

export function PlanUsageSection() {
  const [usage, setUsage] = useState<PlanUsage | null>(null)

  useEffect(() => {
    apiFetch<PlanUsage>('/config/plan-usage').then(setUsage).catch(() => {})
  }, [])

  if (!usage) return null

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-text-muted text-xs font-semibold uppercase tracking-widest">Uso del plan</h2>
        {!usage.enforcement && (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-text-muted bg-background border border-border px-2.5 py-1 rounded-full">
            <Infinity className="w-3 h-3" /> Modo sin límites
          </span>
        )}
      </div>
      {!usage.enforcement && (
        <p className="text-text-muted text-xs flex items-center gap-1.5 -mt-1">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          El proveedor ha desactivado los límites del plan — puedes registrar usuarios, miembros y dispositivos sin restricciones.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {usage.resources.map(r => (
          <ResourceCard key={r.key} r={r} enforcement={usage.enforcement} />
        ))}
      </div>
    </section>
  )
}
